import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptCompetitionPairInvite,
  assignOrganizerCompetitionMatch,
  claimCompetitionRegistration,
  createCompetitionPairInvite,
  generateOrganizerCompetitionDraw,
  getMyCompetitionRegistration,
  getOrganizerCompetitionDraw,
  getOrganizerCompetitionResults,
  getOrganizerCompetitionSchedule,
  getOrganizerCompetitionEntrants,
  getOrganizerCompetitions,
  getPublicCompetition,
  getPublicCompetitionDraw,
  getPublicCompetitionDivisions,
  getPublicCompetitionResults,
  lockOrganizerCompetitionEntrants,
  mergeOrganizerCompetitionEntrants,
  nudgeOrganizerCompetitionMatch,
  previewCompetitionPairInvite,
  publishOrganizerCompetitionDraw,
  registerForCompetition,
  replaceOrganizerCompetitionEntry,
  reviewOrganizerCompetitionEntry,
  resolveOrganizerCompetitionResult,
  completeOrganizerCompetitionDivision,
  downloadOrganizerCompetitionExport,
  getOrganizerCompetitionLaunchReadiness,
  setOrganizerCompetitionRoundDeadline,
  type CompetitionConsent,
  type CompetitionPairInviteRequest,
  type CompetitionRegistrationRequest,
} from '../lib/competitions';

export const competitionKeys = {
  all: ['competitions'] as const,
  public: (slug: string) => [...competitionKeys.all, 'public', slug] as const,
  divisions: (slug: string) => [...competitionKeys.public(slug), 'divisions'] as const,
  publicDraw: (slug: string) => [...competitionKeys.public(slug), 'draw'] as const,
  publicResults: (slug: string) => [...competitionKeys.public(slug), 'results'] as const,
  registration: (competitionPublicId: string) =>
    [...competitionKeys.all, 'registration', competitionPublicId] as const,
  pairPreview: (inviteToken: string) =>
    [...competitionKeys.all, 'pair-preview', inviteToken] as const,
  organizer: () => [...competitionKeys.all, 'organizer'] as const,
  organizerEntrants: (competitionPublicId: string) =>
    [...competitionKeys.organizer(), competitionPublicId, 'entrants'] as const,
  organizerDraw: (competitionPublicId: string) =>
    [...competitionKeys.organizer(), competitionPublicId, 'draw'] as const,
  organizerSchedule: (competitionPublicId: string) =>
    [...competitionKeys.organizer(), competitionPublicId, 'schedule'] as const,
  organizerResults: (competitionPublicId: string) =>
    [...competitionKeys.organizer(), competitionPublicId, 'results'] as const,
  organizerLaunchReadiness: (competitionPublicId: string) =>
    [...competitionKeys.organizer(), competitionPublicId, 'launch-readiness'] as const,
};

export function usePublicCompetition(slug: string) {
  return useQuery({
    queryKey: competitionKeys.public(slug),
    queryFn: () => getPublicCompetition(slug),
    enabled: Boolean(slug),
    staleTime: 60_000,
    retry: false,
  });
}

export function usePublicCompetitionDivisions(slug: string) {
  return useQuery({
    queryKey: competitionKeys.divisions(slug),
    queryFn: () => getPublicCompetitionDivisions(slug),
    enabled: Boolean(slug),
    staleTime: 60_000,
    retry: false,
  });
}

export function usePublicCompetitionDraw(slug: string) {
  return useQuery({
    queryKey: competitionKeys.publicDraw(slug),
    queryFn: () => getPublicCompetitionDraw(slug),
    enabled: Boolean(slug),
    staleTime: 60_000,
    retry: false,
  });
}

export function usePublicCompetitionResults(slug: string, enabled = true) {
  return useQuery({
    queryKey: competitionKeys.publicResults(slug),
    queryFn: () => getPublicCompetitionResults(slug),
    enabled: Boolean(slug) && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useMyCompetitionRegistration(
  competitionPublicId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: competitionKeys.registration(competitionPublicId || ''),
    queryFn: () => getMyCompetitionRegistration(competitionPublicId!),
    enabled: Boolean(competitionPublicId) && enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useCompetitionPairInvitePreview(inviteToken: string | null, enabled: boolean) {
  return useQuery({
    queryKey: competitionKeys.pairPreview(inviteToken || ''),
    queryFn: () => previewCompetitionPairInvite(inviteToken!),
    enabled: Boolean(inviteToken) && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useRegisterForCompetition(competitionPublicId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CompetitionRegistrationRequest) =>
      registerForCompetition(competitionPublicId!, request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.registration(competitionPublicId || ''),
      });
    },
  });
}

export function useCreateCompetitionPairInvite(competitionPublicId: string | undefined) {
  return useMutation({
    mutationFn: (request: CompetitionPairInviteRequest) =>
      createCompetitionPairInvite(competitionPublicId!, request),
  });
}

export function useAcceptCompetitionPairInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      inviteToken,
      clientMutationId,
      consent,
    }: {
      inviteToken: string;
      clientMutationId: string;
      consent: CompetitionConsent;
    }) => acceptCompetitionPairInvite(inviteToken, clientMutationId, consent),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.registration(response.competitionPublicId),
      });
    },
  });
}

export function useClaimCompetitionRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      token,
      clientMutationId,
      consent,
    }: {
      token: string;
      clientMutationId: string;
      consent: CompetitionConsent;
    }) => claimCompetitionRegistration(token, clientMutationId, consent),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.registration(response.competitionPublicId),
      });
    },
  });
}
export function useOrganizerCompetitions(enabled: boolean) {
  return useQuery({
    queryKey: competitionKeys.organizer(),
    queryFn: getOrganizerCompetitions,
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useOrganizerCompetitionEntrants(
  competitionPublicId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: competitionKeys.organizerEntrants(competitionPublicId || ''),
    queryFn: () => getOrganizerCompetitionEntrants(competitionPublicId!),
    enabled: Boolean(competitionPublicId) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useOrganizerCompetitionDraw(
  competitionPublicId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: competitionKeys.organizerDraw(competitionPublicId || ''),
    queryFn: () => getOrganizerCompetitionDraw(competitionPublicId!),
    enabled: Boolean(competitionPublicId) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useOrganizerCompetitionSchedule(
  competitionPublicId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: competitionKeys.organizerSchedule(competitionPublicId || ''),
    queryFn: () => getOrganizerCompetitionSchedule(competitionPublicId!),
    enabled: Boolean(competitionPublicId) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useOrganizerCompetitionResults(
  competitionPublicId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: competitionKeys.organizerResults(competitionPublicId || ''),
    queryFn: () => getOrganizerCompetitionResults(competitionPublicId!),
    enabled: Boolean(competitionPublicId) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useOrganizerCompetitionLaunchReadiness(
  competitionPublicId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: competitionKeys.organizerLaunchReadiness(competitionPublicId || ''),
    queryFn: () => getOrganizerCompetitionLaunchReadiness(competitionPublicId!),
    enabled: Boolean(competitionPublicId) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useDownloadOrganizerCompetitionExport(
  competitionPublicId: string | undefined
) {
  return useMutation({
    mutationFn: () => downloadOrganizerCompetitionExport(competitionPublicId!),
  });
}

function useRefreshOrganizerResults(competitionPublicId: string | undefined) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerResults(competitionPublicId || ''),
      }),
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerSchedule(competitionPublicId || ''),
      }),
      queryClient.invalidateQueries({ queryKey: competitionKeys.organizer() }),
    ]);
  };
}

export function useResolveOrganizerCompetitionResult(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerResults(competitionPublicId);
  return useMutation({
    mutationFn: ({ matchPublicId, request }: {
      matchPublicId: string;
      request: Parameters<typeof resolveOrganizerCompetitionResult>[2];
    }) => resolveOrganizerCompetitionResult(competitionPublicId!, matchPublicId, request),
    onSuccess: refresh,
  });
}

export function useCompleteOrganizerCompetitionDivision(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerResults(competitionPublicId);
  return useMutation({
    mutationFn: ({ divisionPublicId, reason }: { divisionPublicId: string; reason: string }) =>
      completeOrganizerCompetitionDivision(competitionPublicId!, divisionPublicId, {
        clientMutationId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
        reason,
      }),
    onSuccess: refresh,
  });
}

function useRefreshOrganizerSchedule(competitionPublicId: string | undefined) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerSchedule(competitionPublicId || ''),
      }),
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerDraw(competitionPublicId || ''),
      }),
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerLaunchReadiness(competitionPublicId || ''),
      }),
    ]);
  };
}

export function useAssignOrganizerCompetitionMatch(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerSchedule(competitionPublicId);
  return useMutation({
    mutationFn: ({ matchPublicId, request }: {
      matchPublicId: string;
      request: Parameters<typeof assignOrganizerCompetitionMatch>[2];
    }) => assignOrganizerCompetitionMatch(competitionPublicId!, matchPublicId, request),
    onSuccess: refresh,
  });
}

export function useSetOrganizerCompetitionRoundDeadline(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerSchedule(competitionPublicId);
  return useMutation({
    mutationFn: (request: Parameters<typeof setOrganizerCompetitionRoundDeadline>[1]) =>
      setOrganizerCompetitionRoundDeadline(competitionPublicId!, request),
    onSuccess: refresh,
  });
}

export function useNudgeOrganizerCompetitionMatch(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerSchedule(competitionPublicId);
  return useMutation({
    mutationFn: ({ matchPublicId, clientMutationId }: {
      matchPublicId: string;
      clientMutationId: string;
    }) => nudgeOrganizerCompetitionMatch(competitionPublicId!, matchPublicId, clientMutationId),
    onSuccess: refresh,
  });
}

function useRefreshOrganizerCompetition(competitionPublicId: string | undefined) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: competitionKeys.organizer() }),
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerEntrants(competitionPublicId || ''),
      }),
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerDraw(competitionPublicId || ''),
      }),
      queryClient.invalidateQueries({
        queryKey: competitionKeys.organizerLaunchReadiness(competitionPublicId || ''),
      }),
    ]);
  };
}

export function useGenerateOrganizerCompetitionDraw(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerCompetition(competitionPublicId);
  return useMutation({
    mutationFn: (request: Parameters<typeof generateOrganizerCompetitionDraw>[1]) =>
      generateOrganizerCompetitionDraw(competitionPublicId!, request),
    onSuccess: refresh,
  });
}

export function usePublishOrganizerCompetitionDraw(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerCompetition(competitionPublicId);
  return useMutation({
    mutationFn: (request: Parameters<typeof publishOrganizerCompetitionDraw>[1]) =>
      publishOrganizerCompetitionDraw(competitionPublicId!, request),
    onSuccess: refresh,
  });
}

export function useReviewOrganizerCompetitionEntry(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerCompetition(competitionPublicId);
  return useMutation({
    mutationFn: ({ entryPublicId, action, request }: {
      entryPublicId: string;
      action: Parameters<typeof reviewOrganizerCompetitionEntry>[2];
      request: Parameters<typeof reviewOrganizerCompetitionEntry>[3];
    }) => reviewOrganizerCompetitionEntry(competitionPublicId!, entryPublicId, action, request),
    onSuccess: refresh,
  });
}

export function useReplaceOrganizerCompetitionEntry(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerCompetition(competitionPublicId);
  return useMutation({
    mutationFn: ({ entryPublicId, request }: {
      entryPublicId: string;
      request: Parameters<typeof replaceOrganizerCompetitionEntry>[2];
    }) => replaceOrganizerCompetitionEntry(competitionPublicId!, entryPublicId, request),
    onSuccess: refresh,
  });
}

export function useMergeOrganizerCompetitionEntrants(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerCompetition(competitionPublicId);
  return useMutation({
    mutationFn: (request: Parameters<typeof mergeOrganizerCompetitionEntrants>[1]) =>
      mergeOrganizerCompetitionEntrants(competitionPublicId!, request),
    onSuccess: refresh,
  });
}

export function useLockOrganizerCompetitionEntrants(competitionPublicId: string | undefined) {
  const refresh = useRefreshOrganizerCompetition(competitionPublicId);
  return useMutation({
    mutationFn: (request: Parameters<typeof lockOrganizerCompetitionEntrants>[1]) =>
      lockOrganizerCompetitionEntrants(competitionPublicId!, request),
    onSuccess: refresh,
  });
}
